// checkout/utils/stepFocusCallbacks.ts

/**
 * Registry for callbacks that should run when a checkout step becomes FOCUSED.
 * Use `registerStepFocusCallback` to register work and call
 * `executeStepFocusCallbacks` after updating `checkoutData[step].status`.
 */
type StepId = 'loginOrRegister' | 'accountDetails' | 'deliveryAddress' | 'paymentMethod' | string;

const onFocus: Record<StepId, Array<() => void>> = {};

/**
 * Register a callback to run when a step's status is `FOCUSED`.
 * If the step is already focused, runs the callback immediately.
 *
 * @param {StepId} step - Checkout step identifier.
 * @param {() => void} cb - Callback to execute on focus.
 * @returns {void}
 */
export function registerStepFocusCallback(step: StepId, cb: () => void): void {
	const focused = (window as any)?.checkoutData?.[step]?.status === 'FOCUSED';
	if (focused) {
		cb();
		return;
	}
	(onFocus[step] ||= []).push(cb);
}

/**
 * Execute and clear all pending focus callbacks for a step when its
 * status transitions to `FOCUSED`. No-op for other statuses.
 *
 * @param {StepId} step - Checkout step identifier.
 * @param {string} [status] - Current status string (e.g., 'FOCUSED').
 * @returns {void}
 */
export function executeStepFocusCallbacks(step: StepId, status?: string): void {
	if (status !== 'FOCUSED') return;
	const cbs = onFocus[step];
	if (!cbs?.length) return;
	const run = cbs.splice(0, cbs.length);
	run.forEach((cb) => cb());
}
